import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Plus, Users, Loader2, Pencil, Trash2, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  
  const [teamName, setTeamName] = useState("");
  const [teamDescription, setTeamDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");

  useEffect(() => {
    fetchTeams();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      // silently fail — members just won't show
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const updateMembersForTeam = async (teamId, memberIds) => {
    // For each selected member, add this team to their team_ids
    for (const userId of memberIds) {
      const user = users.find(u => u.id === userId);
      if (!user) continue;
      const currentTeams = user.team_ids || [];
      if (!currentTeams.includes(teamId)) {
        await axios.put(`${API}/users/${userId}/teams`, {
          team_ids: [...currentTeams, teamId],
          team_roles: user.team_roles || null,
        });
      }
    }
    // For members removed — remove this team from their team_ids
    const currentMembers = users.filter(u => u.team_ids?.includes(teamId));
    for (const user of currentMembers) {
      if (!memberIds.includes(user.id)) {
        await axios.put(`${API}/users/${user.id}/teams`, {
          team_ids: (user.team_ids || []).filter(tid => tid !== teamId),
          team_roles: user.team_roles || null,
        });
      }
    }
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      toast.error("Team name is required");
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/teams`, {
        name: teamName,
        description: teamDescription || null,
      });
      const newTeamId = res.data?.team?.id;
      if (newTeamId && selectedMembers.length > 0) {
        await updateMembersForTeam(newTeamId, selectedMembers);
      }
      toast.success("Team created successfully!");
      setCreateDialogOpen(false);
      resetForm();
      fetchTeams();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create team");
    } finally {
      setSaving(false);
    }
  };

  const handleEditTeam = async () => {
    if (!teamName.trim()) {
      toast.error("Team name is required");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/teams/${selectedTeam.id}`, {
        name: teamName,
        description: teamDescription || null,
      });
      await updateMembersForTeam(selectedTeam.id, selectedMembers);
      toast.success("Team updated successfully!");
      setEditDialogOpen(false);
      resetForm();
      fetchTeams();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update team");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    try {
      await axios.delete(`${API}/teams/${teamId}`);
      toast.success("Team deleted successfully!");
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete team");
    }
  };

  const openEditDialog = (team) => {
    setSelectedTeam(team);
    setTeamName(team.name);
    setTeamDescription(team.description || "");
    setSelectedMembers(users.filter(u => u.team_ids?.includes(team.id) && u.is_active).map(u => u.id));
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setTeamName("");
    setTeamDescription("");
    setSelectedTeam(null);
    setSelectedMembers([]);
    setMemberSearch("");
  };

  const toggleMember = (userId) => {
    setSelectedMembers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Teams
          </h1>
          <p className="text-slate-600 mt-1">
            Manage teams and organize users
          </p>
        </div>

        <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-team-button">
              <Plus className="mr-2 h-4 w-4" />
              Create Team
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Team</DialogTitle>
              <DialogDescription>
                Add a new team to organize your users.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="teamName">Team Name *</Label>
                <Input
                  id="teamName"
                  placeholder="e.g., ApTask, JobTalk"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  data-testid="team-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="teamDescription">Description (Optional)</Label>
                <Textarea
                  id="teamDescription"
                  placeholder="Brief description of the team"
                  value={teamDescription}
                  onChange={(e) => setTeamDescription(e.target.value)}
                  data-testid="team-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Members ({selectedMembers.length})</Label>
                <Input
                  placeholder="Search users..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="h-8 text-sm"
                />
                <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                  {users.filter(u => u.is_active && u.full_name.toLowerCase().includes(memberSearch.toLowerCase())).sort((a, b) => {
                    const aIn = selectedMembers.includes(a.id) ? 0 : 1;
                    const bIn = selectedMembers.includes(b.id) ? 0 : 1;
                    return aIn - bIn || a.full_name.localeCompare(b.full_name);
                  }).map(u => (
                    <div key={u.id} className={`flex items-center gap-2 px-1.5 py-1 rounded ${selectedMembers.includes(u.id) ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}>
                      <Checkbox
                        id={`create-member-${u.id}`}
                        checked={selectedMembers.includes(u.id)}
                        onCheckedChange={() => toggleMember(u.id)}
                      />
                      <label htmlFor={`create-member-${u.id}`} className="text-sm cursor-pointer flex-1 truncate">{u.full_name}</label>
                      {(u.role === 'ADMIN' || u.role === 'TEAM_LEADER') && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 shrink-0">{u.role === 'ADMIN' ? 'Admin' : 'TL'}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTeam} disabled={saving} data-testid="save-team-button">
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Team"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{teams.length}</p>
                <p className="text-sm text-slate-500">Total Teams</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{teams.reduce((sum, t) => sum + t.member_count, 0)}</p>
                <p className="text-sm text-slate-500">Total Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : teams.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No teams yet</h3>
            <p className="text-slate-500 mt-1">Create your first team to get started</p>
            <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Team
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((team) => (
            <Card key={team.id} className="hover:shadow-md transition-shadow" data-testid={`team-card-${team.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{team.name}</CardTitle>
                    <CardDescription className="mt-1">
                      {team.description || "No description"}
                    </CardDescription>
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge variant="secondary" className="cursor-pointer hover:bg-slate-200 transition-colors">
                        <Users className="h-3 w-3 mr-1" />
                        {team.member_count}
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-0" align="end">
                      <div className="bg-gradient-to-r from-indigo-600 to-blue-500 px-4 py-2.5 rounded-t-lg">
                        <p className="text-sm font-semibold text-white">{team.name}</p>
                        <p className="text-[11px] text-indigo-100">{team.member_count} member{team.member_count !== 1 ? 's' : ''}</p>
                      </div>
                      <div className="p-3">
                      {(() => {
                        const members = users.filter(u => u.team_ids?.includes(team.id) && u.is_active);
                        if (members.length === 0) return <p className="text-xs text-slate-400 italic text-center py-3">No members</p>;

                        const userMap = {};
                        users.forEach(u => { userMap[u.id] = u.full_name; });

                        const groups = {};
                        const unassigned = [];
                        members.forEach(u => {
                          const rt = u.reports_to;
                          const ids = Array.isArray(rt) ? rt : (rt ? [rt] : []);
                          const validIds = ids.filter(id => id && userMap[id]);
                          if (validIds.length === 0) {
                            unassigned.push(u);
                          } else {
                            validIds.forEach(mgrId => {
                              if (!groups[mgrId]) groups[mgrId] = [];
                              groups[mgrId].push(u);
                            });
                          }
                        });

                        const sortedMgrs = Object.entries(groups).sort((a, b) => b[1].length - a[1].length);

                        return (
                          <div className="space-y-3 max-h-72 overflow-y-auto">
                            {sortedMgrs.map(([mgrId, mgrMembers]) => (
                              <div key={mgrId} className="bg-slate-50 rounded-lg p-2.5">
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center">
                                      <Users className="h-3 w-3 text-indigo-600" />
                                    </div>
                                    <span className="text-xs font-semibold text-indigo-700">{userMap[mgrId]}</span>
                                  </div>
                                  <Badge className="bg-indigo-100 text-indigo-700 text-[10px] px-1.5 py-0 h-4 hover:bg-indigo-100">{mgrMembers.length}</Badge>
                                </div>
                                <div className="ml-6 space-y-1">
                                  {mgrMembers.map(u => (
                                    <div key={u.id} className="flex items-center justify-between">
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        <span className="w-1 h-1 rounded-full bg-indigo-300 shrink-0" />
                                        <span className="text-[11px] text-slate-700 truncate">{u.full_name}</span>
                                      </div>
                                      {(u.role === 'ADMIN' || u.role === 'TEAM_LEADER') && (
                                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 text-purple-600 border-purple-200 shrink-0 ml-1">
                                          {u.role === 'ADMIN' ? 'Admin' : 'TL'}
                                        </Badge>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                            {unassigned.length > 0 && (
                              <div className="bg-amber-50 rounded-lg p-2.5">
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center">
                                      <Users className="h-3 w-3 text-amber-600" />
                                    </div>
                                    <span className="text-xs font-semibold text-amber-700">Unassigned</span>
                                  </div>
                                  <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0 h-4 hover:bg-amber-100">{unassigned.length}</Badge>
                                </div>
                                <div className="ml-6 space-y-1">
                                  {unassigned.map(u => (
                                    <div key={u.id} className="flex items-center justify-between">
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        <span className="w-1 h-1 rounded-full bg-amber-300 shrink-0" />
                                        <span className="text-[11px] text-slate-700 truncate">{u.full_name}</span>
                                      </div>
                                      {(u.role === 'ADMIN' || u.role === 'TEAM_LEADER') && (
                                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 text-purple-600 border-purple-200 shrink-0 ml-1">
                                          {u.role === 'ADMIN' ? 'Admin' : 'TL'}
                                        </Badge>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-400">
                    Created {format(parseISO(team.created_at), "MMM d, yyyy")}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(team)}
                      data-testid={`edit-team-${team.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-team-${team.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {team.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove the team and unassign all {team.member_count} members. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteTeam(team.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team</DialogTitle>
            <DialogDescription>
              Update team information.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editTeamName">Team Name *</Label>
              <Input
                id="editTeamName"
                placeholder="e.g., ApTask, JobTalk"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editTeamDescription">Description (Optional)</Label>
              <Textarea
                id="editTeamDescription"
                placeholder="Brief description of the team"
                value={teamDescription}
                onChange={(e) => setTeamDescription(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Members ({selectedMembers.length})</Label>
              <Input
                placeholder="Search users..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                className="h-8 text-sm"
              />
              <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                {users.filter(u => u.is_active && u.full_name.toLowerCase().includes(memberSearch.toLowerCase())).sort((a, b) => {
                  const aIn = selectedMembers.includes(a.id) ? 0 : 1;
                  const bIn = selectedMembers.includes(b.id) ? 0 : 1;
                  return aIn - bIn || a.full_name.localeCompare(b.full_name);
                }).map(u => (
                  <div key={u.id} className={`flex items-center gap-2 px-1.5 py-1 rounded ${selectedMembers.includes(u.id) ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}>
                    <Checkbox
                      id={`edit-member-${u.id}`}
                      checked={selectedMembers.includes(u.id)}
                      onCheckedChange={() => toggleMember(u.id)}
                    />
                    <label htmlFor={`edit-member-${u.id}`} className="text-sm cursor-pointer flex-1 truncate">{u.full_name}</label>
                    {(u.role === 'ADMIN' || u.role === 'TEAM_LEADER') && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 shrink-0">{u.role === 'ADMIN' ? 'Admin' : 'TL'}</Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditTeam} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
